import React, { useMemo, useState } from 'react';
import { BadgeCheck, ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { Certification as CertificationType } from '../../data/profileData';
import { Button, Card, Icon, ResponsiveImage, SectionHeader, getProviderIcon } from '../ui';
import './Certifications.css';

interface CertificationsProps {
  certifications: CertificationType[];
}

const INITIAL_COUNT = 6;
// Vendor certifications, in this order, rank above course certificates.
const VENDORS = ['Microsoft', 'Google', 'Anthropic'];

/** Verified badges first, then vendor certifications (Microsoft, Google, Anthropic), then course certificates. */
const rank = (cert: CertificationType): number => {
  if (cert.badgeImageUrl || cert.verifyUrl) return 0;
  const vendor = VENDORS.indexOf(cert.provider);
  return vendor === -1 ? VENDORS.length + 1 : vendor + 1;
};

// Array.prototype.sort is stable, so content order is kept within each group.
export const orderCertifications = (certifications: CertificationType[]): CertificationType[] =>
  [...certifications].sort((a, b) => rank(a) - rank(b));

const ProviderMark: React.FC<{ cert: CertificationType }> = ({ cert }) => {
  if (cert.badgeImageUrl) {
    return (
      <span className="cert__mark cert__mark--badge">
        <ResponsiveImage path={cert.badgeImageUrl} alt={`${cert.name} badge`} className="cert__badge-img" />
      </span>
    );
  }
  const ProviderIcon = getProviderIcon(cert.provider);
  return (
    <span className="cert__mark">
      <ProviderIcon size={22} />
    </span>
  );
};

/** Certifications as a compact grid: category filter, strongest first, six shown until expanded. */
const Certifications: React.FC<CertificationsProps> = ({ certifications }) => {
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [showAll, setShowAll] = useState(false);

  const ordered = useMemo(() => orderCertifications(certifications), [certifications]);
  const categories = ['All', ...Array.from(new Set(certifications.map((c) => c.category)))];
  const countFor = (category: string) =>
    category === 'All' ? certifications.length : certifications.filter((c) => c.category === category).length;

  const matching = activeCategory === 'All' ? ordered : ordered.filter((c) => c.category === activeCategory);
  const visible = showAll ? matching : matching.slice(0, INITIAL_COUNT);
  const hasMore = matching.length > INITIAL_COUNT;

  const selectCategory = (category: string) => {
    setActiveCategory(category);
    setShowAll(false);
  };

  return (
    <div className="expertise__block certifications" id="certifications">
      <SectionHeader
        id="certifications-heading"
        title="Certifications"
        level={3}
        intro={`${certifications.length} credentials across AI, cloud, and development.`}
      />

      <div className="ui-filter certifications__filter" role="group" aria-label="Filter certifications by category">
        {categories.map((category) => (
          <button
            key={category}
            type="button"
            className="ui-filter__option"
            aria-pressed={activeCategory === category}
            onClick={() => selectCategory(category)}
          >
            {category}
            <span className="ui-filter__count">{countFor(category)}</span>
          </button>
        ))}
      </div>

      <p className="visually-hidden" aria-live="polite">
        {`${visible.length} of ${matching.length} certifications shown.`}
      </p>

      <ul id="certifications-list" className="certifications__grid">
        {visible.map((cert) => (
          <Card as="li" key={cert.id} className="cert">
            <div className="cert__header">
              <ProviderMark cert={cert} />
              <div className="cert__text">
                <h4 className="cert__name">{cert.name}</h4>
                <p className="cert__provider">
                  {cert.provider} · {cert.category}
                </p>
              </div>
            </div>

            {(cert.pdfUrl || cert.verifyUrl) && (
              <div className="cert__links">
                {cert.pdfUrl && (
                  <a
                    href={`${cert.pdfUrl}#view=FitH`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cert__link"
                    aria-label={`Certificate: ${cert.name}`}
                  >
                    <Icon icon={FileText} size={15} />
                    Certificate
                  </a>
                )}
                {cert.verifyUrl && (
                  <a
                    href={cert.verifyUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cert__link"
                    aria-label={`Verify: ${cert.name}`}
                  >
                    <Icon icon={BadgeCheck} size={15} />
                    Verify
                  </a>
                )}
              </div>
            )}
          </Card>
        ))}
      </ul>

      {hasMore && (
        <div className="certifications__more">
          <Button
            variant="secondary"
            size="sm"
            icon={<Icon icon={showAll ? ChevronUp : ChevronDown} size={16} />}
            onClick={() => setShowAll((all) => !all)}
            aria-expanded={showAll}
            aria-controls="certifications-list"
          >
            {showAll ? 'Show fewer' : `Show all ${matching.length} certifications`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Certifications;
